import {Injectable, OnDestroy} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {BehaviorSubject, first, Observable, Subject, Subscription, timer} from "rxjs";
import {PivotGridAxisData, PivotGridData} from "./pivot-grid.data";
import {HeadingCellData, PivotData} from "./pivot.data";
import {PivotQueryRequestData} from "../../framework/bi/pivot-query-request.data";
import {BIService} from "../../framework/bi/bi.service";
import {CubeDefinitionData} from "../../framework/bi/cube-definition.data";
import {EntityData} from "@fedeltapointofsale/data-client";
import {MdxMemberUtil} from "../../framework/bi/mdx-member-util";
import {OlapPivotResultsData} from "../../framework/bi/olap-pivot-results.data";
import {RestrictionsResponseData} from "../../framework/bi/restrictions-response.data";
import {RestrictionsRequestData, RestrictionsRequestQueryData} from "../../framework/bi/restrictions-request.data";
import {ReportGenerationService} from "../../framework/entity/report/report-generation.service";
import {PivotGridReportModel} from "./pivot-grid-report.model";
import {StringUtils} from "../../framework/utils/string-utils";

@Injectable()
export class PivotGridModel implements OnDestroy {

    pivotGridData: PivotGridData;
    cube: CubeDefinitionData;

    data: PivotData;

    restriction$: Subject<RestrictionsResponseData> = new BehaviorSubject(undefined);

    /**
     * Define when there's a query running
     * @type {BehaviorSubject<boolean>}
     */
    queryRunning$: Subject<boolean> = new BehaviorSubject(false);

    runningRequestId: string;
    runningServiceSubscription: Subscription;
    pollSubscription: Subscription;
    runningResultSubject: Subject<OlapPivotResultsData>;

    /**
     * Indicates if the dimension title should be shown in the grid which allows the dimension to be collapsed
     * and expanded
     */
    public showDimension = false;

    /**
     * Indicates if the dimensions should be expanded as they are added to the grid
     */
    public openAllDimensions = false;

    /**
     * Indicates that the grid data has changed
     */
    public dataUpdatedSubject: Subject<void> = new Subject<void>();

    reportGenerationService: ReportGenerationService;

    constructor(private biService: BIService,
                private translateService: TranslateService,
                private pivotGridReportModel: PivotGridReportModel) {
    }

    public init(cube: CubeDefinitionData, pivotGridData: PivotGridData, reportGenerationService: ReportGenerationService): void {
        this.cube = cube;
        this.pivotGridData = pivotGridData;
        this.reportGenerationService = reportGenerationService;
    }

    /**
     * Reloads the grid with the latest data
     */
    public refresh(): void {
        // load data
        const req: PivotQueryRequestData = this.createRequest();

        this.queryPivot(req, false).subscribe((olapData: OlapPivotResultsData) => {
            this.data = new PivotData(olapData, this.translateService);

            this.dataUpdatedSubject.next();
        });
    }

    private createRequest(): PivotQueryRequestData {
        const req: PivotQueryRequestData = new PivotQueryRequestData();
        req.requestId = StringUtils.uuid();
        req.cubeName = this.cube.cubeName;
        req.columns = this.pivotGridData.columns.map(value => value.dimensions);
        req.columnMeasures = this.pivotGridData.columnMeasures;
        req.rows = this.pivotGridData.rows.map(value => value.dimensions);
        req.rowMeasures = this.pivotGridData.rowMeasures;
        req.slicers = this.pivotGridData.slicers;
        req.dateRangeRef = this.pivotGridData.dateRangeRef;
        return req;
    }

    public drillColumn(name: string, colIdx: number, depth: number): void {
        const colHeading: HeadingCellData = this.data.colHeadings[colIdx][depth];

        // Add parent members
        const members: string[] = [];
        colHeading.parentMembers.forEach((t: string) => {
            members.push(t);
        });

        // Add target name
        members.push(name + ".children");

        // Add children members
        let found: boolean = false;
        this.pivotGridData.columns[0].dimensions.forEach((t: string) => {
            if (found) {
                members.push(t);
            }

            if (!found && MdxMemberUtil.getDimensionKey(name) === MdxMemberUtil.getDimensionKey(t)) {
                found = true;
            }
        });

        const column = <PivotGridAxisData>EntityData.create(PivotGridAxisData);
        column.dimensions = members;
        this.pivotGridData.columns.push(column);

        const columnReq: string[][] = [];
        columnReq.push(members);

        const req: PivotQueryRequestData = this.createRequest();
        req.columns = columnReq;

        this.queryPivot(req).subscribe((data: OlapPivotResultsData) => {
            if (data && data.values && data.values.length > 0) {
                this.data.mergeDrillOnColumn(data, colHeading);
                this.dataUpdatedSubject.next();
            } else {
                this.pivotGridData.columns.splice(this.pivotGridData.columns.length - 1, 1);
                colHeading.openStatus = undefined;
                colHeading.noChildData = true;
            }

        }, (e) => {
            this.pivotGridData.columns.splice(this.pivotGridData.columns.length - 1, 1);
            throw e;
        });
    }

    queryPivot(request: PivotQueryRequestData, modal: boolean = true): Observable<OlapPivotResultsData> {
        this.cancelRunningRequest();

        const restrictionsRequestData: RestrictionsRequestData = new RestrictionsRequestData();
        restrictionsRequestData.cubeName = request.cubeName;
        restrictionsRequestData.dateRangeRef = request.dateRangeRef;
        restrictionsRequestData.dashboardAttributes = request.slicers.map(value => MdxMemberUtil.getDimension(value));

        const query: RestrictionsRequestQueryData = new RestrictionsRequestQueryData();
        query.queryAttributes = [];
        request.rows.forEach(rows => rows.filter(row => row.indexOf("children") == -1).forEach(column => query.queryAttributes.push(column)));
        request.columns.forEach(columns => columns.filter(column => column.indexOf("children") == -1).forEach(column => query.queryAttributes.push(column)));
        query.name = "pivot";

        restrictionsRequestData.queries = [query];

        const result: Subject<OlapPivotResultsData> = new Subject();

        this.queryRunning$.next(true);

        this.biService.getRestrictions(restrictionsRequestData).then(restriction => {
            this.restriction$.next(restriction);

            if (restriction.datesRestricted) {
                request.restrictedStartDate = restriction.availableStartDate;
                request.restrictedEndDate = restriction.availableEndDate;
                request.restrictedDateLevel = restriction.restrictedDateLevel;
            }

            this.runningRequestId = request.requestId;
            this.runningResultSubject = result;

            this.load(request, result);

        }).catch(error => {
            result.error(error);
            this.queryRunning$.next(false);
            throw error;
        });

        return result;
    }

    initNextPool(req: PivotQueryRequestData, result: Subject<OlapPivotResultsData>): void {
        this.pollSubscription = timer(3000).pipe(first()).subscribe(() => this.load(req, result));
    }

    private load(request: PivotQueryRequestData, result: Subject<OlapPivotResultsData>): void {

        if (!this.runningRequestId) {
            return;
        }

        this.runningServiceSubscription = this.biService.queryPivot(request).subscribe((response: OlapPivotResultsData) => {
            if (response.pending) {
                this.runningServiceSubscription.unsubscribe();
                request.resubmission = true;
                this.initNextPool(request, result);
                return;
            }

            this.clearRunningRequest();
            this.queryRunning$.next(false);
            result.next(response);
            result.complete();
        }, error => {
            this.clearRunningRequest();
            this.queryRunning$.next(false);
            result.error(error);
            result.complete();
        });
    }


    public drillRow(name: string, rowIdx: number, depth: number): void {
        const rowHeading: HeadingCellData = this.data.rowHeadings[rowIdx][depth];

        // Add parent members
        const members: string[] = [];
        rowHeading.parentMembers.forEach((t: string) => {
            members.push(t);
        });

        // Add target name
        members.push(name + ".children");

        // Add children members
        let found: boolean = false;
        this.pivotGridData.rows[0].dimensions.forEach((t: string) => {
            if (found) {
                members.push(t);
            }

            if (!found && MdxMemberUtil.getDimensionKey(name) === MdxMemberUtil.getDimensionKey(t)) {
                found = true;
            }

        });

        const row = <PivotGridAxisData>EntityData.create(PivotGridAxisData);
        row.dimensions = members;

        this.pivotGridData.rows.push(row);

        const rowReq: string[][] = [];
        rowReq.push(members);

        const req: PivotQueryRequestData = this.createRequest();
        req.rows = rowReq;

        this.queryPivot(req).subscribe((data: OlapPivotResultsData) => {
            if (data && data.values && data.values.length > 0) {
                this.data.mergeDrillOnRow(data, rowHeading);
                this.dataUpdatedSubject.next();
            } else {
                this.pivotGridData.rows.splice(this.pivotGridData.rows.length - 1, 1);
                rowHeading.openStatus = undefined;
                rowHeading.noChildData = true;
            }
        }, (e) => {
            this.pivotGridData.rows.splice(this.pivotGridData.rows.length - 1, 1);
            throw e;
        });
    }

    public drillUpColumn(name: string, levelDepth: number, colIdx: number, depth: number): void {
        const colHeading: HeadingCellData = this.data.colHeadings[colIdx][depth];

        let memberAtDepth = MdxMemberUtil.getMemberAtDepth(name, levelDepth);
        if (MdxMemberUtil.isLevel(memberAtDepth)) {
            memberAtDepth = MdxMemberUtil.getHierarchy(memberAtDepth);
        }

        this.removeCriteria(this.pivotGridData.columns, memberAtDepth, colHeading.parentMembers);

        this.data.removeColumnChildren(memberAtDepth, colHeading.parentMembers);
        this.dataUpdatedSubject.next();
    }


    public drillUpRow(name: string, levelDepth: number, rowIdx: number, depth: number): void {
        const rowHeading: HeadingCellData = this.data.rowHeadings[rowIdx][depth];

        let memberAtDepth = MdxMemberUtil.getMemberAtDepth(name, levelDepth);

        if (MdxMemberUtil.isLevel(memberAtDepth)) {
            memberAtDepth = MdxMemberUtil.getHierarchy(memberAtDepth);
        }

        this.removeCriteria(this.pivotGridData.rows, memberAtDepth, rowHeading.parentMembers);

        this.data.removeRowChildren(memberAtDepth, rowHeading.parentMembers);
        this.dataUpdatedSubject.next();
    }

    /**
     * Replace slicer members and refresh
     * @param {string[]} filterMembers
     * @param dateRange
     */
    public updateSlicer(filterMembers: string[], dateRange: string): void {
        this.pivotGridData.slicers = filterMembers;
        this.pivotGridData.dateRangeRef = dateRange;
        this.refresh();
    }

    /**
     * Replace column members
     * #refresh must be called
     * @param {string[]} columns columns
     */
    public updateColumns(columns: string[][]): void {
        this.pivotGridData.columns = columns.map(value => {
            const column = <PivotGridAxisData>EntityData.create(PivotGridAxisData);
            column.dimensions = value;
            return column;
        });
    }

    /**
     * Replace row members
     * #refresh must be called
     * @param {string[]} rows rows
     */
    public updateRows(rows: string[][]): void {
        this.pivotGridData.rows = rows.map(value => {
            const row = <PivotGridAxisData>EntityData.create(PivotGridAxisData);
            row.dimensions = value;
            return row;
        });
    }

    /**
     * Replace measures
     * #refresh must be called
     * @param {string[]} measures
     */
    public updateMeasures(measures: string[]): void {
        this.pivotGridData.columnMeasures = measures;
    }

    /**
     * Download the current grid as an excel
     */
    public downloadExcel() {
        const req: PivotQueryRequestData = this.createRequest();

        if (!this.reportGenerationService.running) {
            this.pivotGridReportModel.data = req;

            this.reportGenerationService.execute(this.pivotGridReportModel);
        }
    }

    public showDimensions() {
        this.showDimension = true;
        this.dataUpdatedSubject.next();
    }

    public hideDimensions() {
        this.showDimension = false;
        this.dataUpdatedSubject.next();
    }


    private removeCriteria(headingMembers: PivotGridAxisData[], memberAtDepth: string, parentMembers: string[]) {
        const memberPrefix = memberAtDepth + ".";

        let parentMember: string;
        if (parentMembers && parentMembers.length > 0) {
            parentMember = parentMembers[parentMembers.length - 1];
        }

        for (let i = headingMembers.length - 1; i >= 0; i--) {

            let found: boolean = false;
            headingMembers[i].dimensions.forEach((t: string, i2: number) => {
                if (t.startsWith(memberPrefix)) {
                    if (!parentMember || (i2 !== 0 && headingMembers[i][i2 - 1] === parentMember)) {
                        found = true;
                    }
                }
            });

            if (found) {
                headingMembers.splice(i, 1);
            }
        }
    }

    ngOnDestroy(): void {
        this.cancelRunningRequest();
    }

    cancelRunningRequest(): void {
        if (this.runningRequestId) {
            this.biService.cancelQuery(this.runningRequestId);
            this.runningServiceSubscription.unsubscribe();
            this.runningResultSubject.complete();
            this.clearRunningRequest();
        }
    }

    clearRunningRequest(): void {
        this.runningRequestId = undefined;
        this.runningServiceSubscription = undefined;
        this.runningResultSubject = undefined;

        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
            this.pollSubscription = undefined;
        }
    }
}
